import { Autocomplete, Button, Card, CardContent, Divider, FormControl, FormControlLabel, FormLabel, IconButton, Link, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import Loading from "components/Loading";
import { useLogin } from "providers/Login";
import { useNotification } from "providers/Notification";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import EditObfuscation from "../Obfuscations/EditObfuscation";
import { AddNewApplication } from "../Apps/AddApplication";
import {  HeaderFilter, HeaderFilterValue } from "./_components/HeaderFilter";
import { ListSelect } from "../../../../components/ListSelect";
import { PaperWithFooter, findOptions, footerSlotProps, isChipKeyboardRemoval, optionValuesEqual, optionsFromDict, renderOptionChips, renderOptionWithHighlight, searchTextField, sortOptionsAlphabetically } from "../../../../utils/searchableOptions";
import { ShieldInstancesSelect } from "components/ShieldInstancesSelect";
import { HeaderConditionType } from "./_components/BaseFilter";
import { GroupFilter } from "./_components/GroupFilter";
import { UsernameFilter } from "./_components/UsernameFilter";
import { TimeFilter, TimeFilterConfig } from "./_components/TimeFilter";
import { useRole } from "hooks/useRole";
import { ReadOnlyNotice } from "components/ReadOnlyNotice";

// Rule action types
type RuleAction = "obfuscate" | "block" | "detect";

interface Rule {
    id?: string
    name: string
    description: string
    order?: number,
    action: RuleAction
    isBlockRequestRule?: boolean  // DEPRECATED: Keep for backwards compatibility
    enable: boolean
    apps: string[]
    obfuscations: string[]
    icapInstances: string[]
    icapMode: string
    filters: (GroupFilter | UsernameFilter)[]
    timeFilterEnabled?: boolean
    timeFilter?: TimeFilterConfig
}

function RuleToAPIPayload(o: Rule) {
    let rule: any = {
        name: o.name,
        description: o.description,
        enable: o.enable,
        action: o.action,
        isBlockRequestRule: o.action === "block",  // Keep for backwards compatibility
        ruleOrder: o.order,
        apps: o.apps,
        icapMode: o.icapMode,
        icapInstances: o.icapInstances.join(","),
        obfuscations: o.obfuscations,
        groupFilters: (o.filters.filter(x => x.filterType === "group") as GroupFilter[]).map(x => {
            const exact = x.conditionType === HeaderConditionType.Equal || x.conditionType === HeaderConditionType.NotEqual;
            return {
                condition: x.conditionType,
                filter: x.filter,
                groupId: exact ? x.groupId : "",
            };
        }),
        userFilters: (o.filters.filter(x => x.filterType === "username") as UsernameFilter[]).map(x => {
            return {
                condition: x.conditionType,
                filter: x.filter,
            };
        }),
        timeFilterEnabled: o.timeFilterEnabled || false,
    };

    if (o.timeFilterEnabled && o.timeFilter) {
        rule.timeFilter = o.timeFilter;
    }

    return rule;
}

function APIPayloadToRule(i: any): Rule {
    // Determine action with backwards compatibility
    let action: RuleAction = "obfuscate";
    if (i.action) {
        action = i.action as RuleAction;
    } else if (i.isBlockRequestRule) {
        action = "block";
    }

    let rule: Rule = {
        name: i.name,
        description: i.description,
        action: action,
        isBlockRequestRule: i.isBlockRequestRule,  // Keep for backwards compatibility
        enable: i.enable,
        order: i.ruleOrder,
        apps: i.apps || [],
        obfuscations: i.obfuscations || [],
        icapMode: i.icapMode || (action === "block" ? "BOTH" : "RESPMOD"),
        icapInstances: i.icapInstances ? i.icapInstances.split(",") : [],
        filters:
            i.userFilters.map((x: any) => {
                let ret: UsernameFilter = {
                    filterType: "username",
                    conditionType: x.condition,
                    filter: x.filter,
                };
                return ret;
            }).concat(i.groupFilters.map((x: any) => {
                let ret: GroupFilter = {
                    filterType: "group",
                    conditionType: x.condition,
                    filter: x.filter,
                    groupId: x.groupId || undefined,
                    groupName: x.groupName || undefined,
                    directoryName: x.directoryName || undefined,
                    groupHidden: x.groupHidden,
                };
                return ret;
            })),
        timeFilterEnabled: i.timeFilterEnabled || false,
        timeFilter: i.timeFilter || undefined,
    };
    return rule;
}

interface EditRuleProps {
    ruleId?: string
    onSave?: (app: Rule) => void
    onExit?: () => void
}

type AppsList = { [key: string]: string };
type ObfuscationsList = { [key: string]: string };

export default function EditRule(props: EditRuleProps) {
    const login = useLogin();
    const notification = useNotification();
    const { canEditPolicy } = useRole();

    const [currentScreen, setCurrentScreen] = useState("AddRule");
    const [ready, setReady] = useState(false);
    const [saving, setSaving] = useState(false);

    const [name, setName] = useState("");
    const [action, setAction] = useState<RuleAction>("obfuscate");
    const [description, setDescription] = useState("");
    const [ruleOrder, setRuleOrder] = useState(0);
    const [enabled, setEnabled] = useState(true);
    const [icapMode, setIcapMode] = useState("RESPMOD");
    const [apps, setApps] = useState([] as string[]);
    const [obfuscations, setObfuscations] = useState([] as string[]);
    const [icapInstances, setIcapInstances] = useState([] as string[]);

    const [filters, setFilters] = useState<(GroupFilter | UsernameFilter)[]>([]);
    const [timeFilter, setTimeFilter] = useState<TimeFilterConfig | undefined>(undefined);
    const [isTimeFilterValid, setIsTimeFilterValid] = useState<boolean>(true);

    const [appsList, setAppsList] = useState({} as AppsList);
    const [obfuscationsList, setObfuscationsList] = useState({} as ObfuscationsList);

    const loadData = async (loadRule = true) => {
        const req = login.GetAxios();

        if (!req) {
            notification.Display({
                type: "error",
                title: "Request Failed",
                message: "could not make request",
            });
            return;
        }

        try {
            const appsResp = await req.get("/api/apps", {
                params: {
                    sortBy: "name",
                    sortDirection: "asc"
                }
            });
            const appsTmp: AppsList = {};
            (appsResp.data.items || []).forEach((x: any) => {
                appsTmp[x.id] = x.name;
            });
            setAppsList(appsTmp);

            const obfuscationsResp = await req.get("/api/obfuscations", {
                params: {
                    sortBy: "name",
                    sortDirection: "asc"
                }
            });
            const obfuscationsTmp: ObfuscationsList = {};
            (obfuscationsResp.data.items || []).forEach((x: any) => {
                obfuscationsTmp[x.id] = x.name;
            });
            setObfuscationsList(obfuscationsTmp);

            if (props.ruleId && loadRule) {
                const ruleReq = await req.get(`/api/rules/${props.ruleId}`);

                const rule = APIPayloadToRule(ruleReq.data);

                setName(rule.name);
                setDescription(rule.description);
                setEnabled(rule.enable);
                setIcapMode(rule.icapMode);
                setAction(rule.action);
                setRuleOrder(rule.order!);
                setApps(rule.apps);
                setObfuscations(rule.obfuscations);
                setIcapInstances(rule.icapInstances);
                setFilters(rule.filters);
                setTimeFilter(rule.timeFilter);
            }

        } catch (err) {
            notification.Display({
                type: "error",
                title: "Request Failed",
                message: "could not make request",
            });
            return;
        }

        setReady(true);
    };

    useEffect(() => {
        loadData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const validate = () => {
        if (!name || !name.trim()) {
            notification.Display({
                type: "error",
                title: "Validation Error",
                message: "Name is required."
            });

            return false;
        }

        if (!apps || !apps.length) {
            notification.Display({
                type: "error",
                title: "Validation Error",
                message: "There should be at least 1 Application for a Rule."
            });

            return false;
        }

        if (!isTimeFilterValid) {
            notification.Display({
                type: "error",
                title: "Validation Error",
                message: "Please ensure the time filter configuration is valid."
            });

            return false;
        }

        // Obfuscations are required for "obfuscate" and "detect" actions
        if (action === "obfuscate" || action === "detect") {
            if (!obfuscations || !obfuscations.length) {
                notification.Display({
                    type: "error",
                    title: "Validation Error",
                    message: `There should be at least 1 Obfuscation for a Rule with ${action === "obfuscate" ? "Obfuscate" : "Detect"} action.`
                });

                return false;
            }
        }

        return true;
    };

    const save = async () => {
        const req = login.GetAxios();

        if (!req) {
            notification.Display({
                type: "error",
                title: "Request Failed",
                message: "could not make request",
            });
            return;
        }

        if (!validate()) {
            return;
        }

        setSaving(true);

        try {
            if (!props.ruleId) {
                //create
                const resp = await req.post("/api/rules",
                    RuleToAPIPayload({
                        name: name,
                        description: description,
                        apps: apps,
                        enable: true,
                        action: action,
                        obfuscations: obfuscations,
                        filters: filters,
                        icapMode: icapMode,
                        icapInstances,
                        timeFilterEnabled: !!timeFilter,
                        timeFilter: timeFilter,
                    })
                );

                const addedApp = APIPayloadToRule(resp.data);

                props.onSave && props.onSave(addedApp);

                notification.Display({
                    type: "success",
                    title: "Success",
                    message: "Rule saved successfully",
                });
            } else {
                //update
                const resp = await req.put(`/api/rules/${props.ruleId}`,
                    RuleToAPIPayload({
                        name: name,
                        description: description,
                        apps: apps,
                        order: ruleOrder,
                        enable: enabled,
                        action: action,
                        obfuscations: obfuscations,
                        filters: filters,
                        icapMode: icapMode,
                        icapInstances,
                        timeFilterEnabled: !!timeFilter,
                        timeFilter: timeFilter,
                    })
                );

                const addedApp = APIPayloadToRule(resp.data);

                props.onSave && props.onSave(addedApp);

                notification.Display({
                    type: "success",
                    title: "Success",
                    message: "Rule saved successfully",
                });
            }

        } catch (err) {
            setSaving(false);
            notification.Display({
                type: "error",
                title: "Request Failed",
                message: "could not save app",
            });
            return;
        }
    };

    const exit = () => {
        props.onExit && props.onExit();
    };

    const applyHeaderFilterValue = (filter: HeaderFilterValue) => {
        if (filter.groupFilter) {
            setFilters(filters.concat(filter.groupFilter));
        }

        if (filter.usernameFilter) {
            setFilters(filters.concat(filter.usernameFilter));
        }
    };

    const getHeaderFilterText = (filter: GroupFilter | UsernameFilter) => {
        const options = {
            "username": "Username",
            "group": "Group",
        };

        let headerText = options[filter.filterType];
        const conditionText: { [key in HeaderConditionType]: string } = {
            "": "",
            equal: "equals",
            not_equal: "does not equal",
            contains: "contains",
            not_contains: "does not contain",
        };
        if (filter.filterType === "group") {
            const exact = filter.conditionType === HeaderConditionType.Equal || filter.conditionType === HeaderConditionType.NotEqual;
            if (exact && filter.groupId && !filter.groupName) {
                return `${headerText} ${conditionText[filter.conditionType]} Unknown group`;
            }
            const directory = exact && filter.groupName && filter.directoryName ? ` (${filter.directoryName})` : "";
            return `${headerText} ${conditionText[filter.conditionType]} "${filter.filter}"${directory}`;
        }

        return `${headerText} ${conditionText[filter.conditionType]} "${filter.filter}"`;
    };


    const deleteHeaderFilter = (key: number) => {
        setFilters(filters.filter((_, i) => i !== key));
    };

    const appOptions = sortOptionsAlphabetically(optionsFromDict(appsList));
    const obfuscationOptions = sortOptionsAlphabetically(optionsFromDict(obfuscationsList));

    return <Loading finished={ready}>
        <>
            {
                (currentScreen === "AddRule" &&
                    <Stack id="add-rule-page" sx={{ flex: 1, height: "100%" }}>
                        <input id="focus-utility" type="hidden"></input>
                        <Box display="flex" sx={{ alignItems: "start" }}>
                            <Box>
                                <Typography sx={{ marginTop: "5px" }} variant="h2">{props.ruleId ? "Edit Rule" : "Add New Rule"}</Typography>
                            </Box>
                            <IconButton
                                id="exit-button"
                                size="small"
                                sx={{ marginLeft: "auto" }}
                                onClick={exit}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <ReadOnlyNotice />

                        <TextField sx={{ marginTop: "20px" }} id="name-input" name="name" label="Name" variant="outlined"
                            value={name}
                            onChange={x => setName(x.target.value)}
                        />

                        <TextField sx={{ marginTop: "20px" }} id="description-input" name="description" label="Description" variant="outlined"
                            value={description}
                            onChange={x => setDescription(x.target.value)}
                        />

                        <Card sx={{ marginTop: "20px", padding: "0px", overflow: "visible" }}>
                            <CardContent sx={{ padding: "10px 20px", paddingBottom: "10px!important" }}>
                                <Stack direction={"column"} spacing={2}>
                                    <FormControl>
                                        <FormLabel sx={{ fontWeight: "bold" }}>Action</FormLabel>
                                        <RadioGroup
                                            row
                                            aria-labelledby="action-radio-buttons-group-label"
                                            name="action-radio-buttons-group"
                                            value={action}
                                            onChange={(e) => {
                                                const newAction = e.target.value as RuleAction;
                                                setAction(newAction);
                                                // Set default ICAP mode based on action
                                                if (newAction === "block") {
                                                    setIcapMode("BOTH");
                                                } else {
                                                    setIcapMode("RESPMOD");
                                                }
                                            }}
                                        >
                                            <FormControlLabel value="obfuscate" control={<Radio />} label="Obfuscate" />
                                            <FormControlLabel value="block" control={<Radio />} label="Block" />
                                            <FormControlLabel value="detect" control={<Radio />} label="Detect" />
                                        </RadioGroup>
                                    </FormControl>
                                    <FormControl sx={{ marginTop: "20px" }}>
                                        <FormLabel sx={{ fontWeight: "bold" }}>Apply to</FormLabel>
                                        <RadioGroup
                                            row
                                            aria-labelledby="apply-to-radio-buttons-group-label"
                                            name="apply-to-radio-buttons-group"
                                            value={icapMode}
                                            onChange={(e) => {
                                                setIcapMode(e.target.value);
                                            }}
                                        >
                                            <FormControlLabel value="REQMOD" control={<Radio />} label="Request" />
                                            <FormControlLabel value="RESPMOD" control={<Radio />} label="Response" />
                                            <FormControlLabel value="BOTH" control={<Radio />} label="Both" />
                                        </RadioGroup>
                                    </FormControl>
                                </Stack>
                            </CardContent>
                        </Card>

                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            disableClearable
                            fullWidth
                            sx={{ marginTop: "20px" }}
                            id="application-select"
                            options={appOptions}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={optionValuesEqual}
                            value={findOptions(appOptions, apps)}
                            onChange={(event, options, reason) => {
                                if (isChipKeyboardRemoval(event, reason)) {
                                    return;
                                }
                                setApps(options.map(x => x.value));
                            }}
                            renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, apps.some(v => v === option.value))}
                            renderTags={(value, getTagProps) => renderOptionChips(value, getTagProps)}
                            renderInput={(params) => searchTextField(params, { label: "Applications" })}
                            PaperComponent={PaperWithFooter}
                            // NS-753
                            //the shortcut opens a create form, which a read-only role cannot save
                            slotProps={footerSlotProps(canEditPolicy &&
                                <Box sx={{
                                    height: "40px",
                                    backgroundColor: "custom.highlight",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "5px"
                                }}>
                                    <Typography>Want to add an application?</Typography>
                                    <Link id="add-application-button" component="button" onClick={() => setCurrentScreen("AddApp")}>Click here</Link>
                                </Box>
                            )}
                        />

                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            disableClearable
                            fullWidth
                            sx={{ marginTop: "20px" }}
                            id="obfuscation-select"
                            options={obfuscationOptions}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={optionValuesEqual}
                            value={findOptions(obfuscationOptions, obfuscations)}
                            onChange={(event, options, reason) => {
                                if (isChipKeyboardRemoval(event, reason)) {
                                    return;
                                }
                                setObfuscations(options.map(x => x.value));
                            }}
                            renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, obfuscations.some(v => v === option.value))}
                            renderTags={(value, getTagProps) => renderOptionChips(value, getTagProps)}
                            renderInput={(params) => searchTextField(params, { label: "Obfuscations" })}
                            PaperComponent={PaperWithFooter}
                            slotProps={footerSlotProps(canEditPolicy &&
                                <Box sx={{
                                    height: "40px",
                                    backgroundColor: "custom.highlight",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "5px"
                                }}>
                                    <Typography>Want to add an obfuscation?</Typography>
                                    <Link id="add-obfuscation-button" component="button" onClick={() => setCurrentScreen("AddObfuscation")}>Click here</Link>
                                </Box>
                            )}
                        />

                        <ListSelect
                            id="header-filter-select"
                            label="Users, Groups and Channels"
                            valueList={filters.map(x => getHeaderFilterText(x)).reduce((prev, current, idx) => {
                                return { ...prev, [idx]: current };
                            }, {})}
                            strikethrough={filters.reduce((prev, x, idx) => {
                                return { ...prev, [idx]: x.filterType === "group" && !!x.groupHidden };
                            }, {} as { [key: string]: boolean })}
                            value={filters.map((_, idx) => idx.toString())}
                            onDelete={x => deleteHeaderFilter(Number(x))}
                            popoverOnly={true}
                            popoverExtra={(handleClose) => {
                                return (<HeaderFilter handleClose={handleClose} onApply={applyHeaderFilterValue} />);
                            }}
                        />

                        <ShieldInstancesSelect
                            sx={{ marginTop: "20px" }}
                            value={icapInstances}
                            onChange={setIcapInstances}
                        />

                        <TimeFilter
                            value={timeFilter}
                            onChange={setTimeFilter}
                            onValidationChange={setIsTimeFilterValid}
                        />

                        <Divider sx={{ marginTop: "auto" }} />
                        <Box display="flex" sx={{ marginTop: "10px" }}>
                            <Button
                                id="back-button"
                                variant="outlined"
                                size="medium"
                                onClick={exit}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                id="save-button"
                                loading={saving}
                                // NS-753
                                disabled={!canEditPolicy}
                                sx={{ marginLeft: "auto" }}
                                variant="contained"
                                size="medium"
                                onClick={save}
                            >
                                Save
                            </LoadingButton>
                        </Box>
                    </Stack>
                ) ||
                (currentScreen === "AddApp" &&
                    <AddNewApplication
                        onBackButtonClicked={() => {
                            setCurrentScreen("AddRule");
                        }}
                        onExit={() => {
                            setCurrentScreen("AddRule");
                        }}
                        onSave={() => {
                            loadData(false);
                            setCurrentScreen("AddRule");
                        }}
                    />
                ) ||
                (currentScreen === "AddObfuscation" &&
                    <EditObfuscation
                        onBackButtonClicked={() => {
                            setCurrentScreen("AddRule");
                        }}
                        onExit={() => {
                            setCurrentScreen("AddRule");
                        }}
                        onSave={() => {
                            loadData(false);
                            setCurrentScreen("AddRule");
                        }}
                    />
                )
            }
        </>
    </Loading>;
}
